import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { api } from "../lib/api";
import {
  GraduationCap, LayoutDashboard, Calculator, BookOpen, Package, Repeat,
  Scale, History, MessagesSquare, BookOpenCheck, User, KeyRound,
  ChevronRight, Search, Rocket, ShieldCheck, Star, Utensils, Users,
  Flame, Pill, MessageSquare, Trophy, Activity, Megaphone, Palette, Crown,
  UserPlus, UserCog, ShieldAlert, Newspaper, Send, Flag, Ban, Database,
  Clock, LayoutList, Eye, Pin, Camera, Mic, Sparkles, Tag,
} from "lucide-react";
import { Input } from "../components/ui/input";

/* ---------------- Tutorial data — USER role (default) ---------------- */
const userSections = [
  {
    id: "getting-started",
    title: "Getting started",
    icon: Rocket,
    intro: "The 60-second orientation. Do these three things first and everything else clicks into place.",
    steps: [
      { h: "Set your profile", body: "Open Profile from the avatar menu (top-right). Enter body weight, height, default dose interval, timezone, and your macro goals. This drives Dashboard countdowns, dose reminders, and nutrition targets.", link: "/profile", cta: "Open Profile" },
      { h: "Stock your first vial", body: "Head to Inventory → Add Vial. Enter the peptide, mg strength, and BAC water (mL). Every shot you log will auto-deduct from the oldest matching vial (FIFO).", link: "/inventory", cta: "Open Inventory", flag: "inventory" },
      { h: "Log your first shot", body: "Tap the floating teal “+” button (visible on every page) or use Dashboard → Log Dose. Pick peptide, dose, site on the body diagram, and time. Your next-dose countdown starts immediately.", link: "/dashboard", cta: "Back to Dashboard" },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    intro: "Your control room. Countdowns, streaks, weekly totals, weight trend, and per-peptide next-dose timers all live here.",
    steps: [
      { h: "Next-dose countdown hero", body: "The big timer at the top of the Dashboard counts down to your next scheduled dose. Turns red when overdue, green when fresh. Powered by your default interval + last logged shot." },
      { h: "Per-peptide countdowns", body: "Each active peptide gets its own timer showing hours & minutes until the next dose. Tap a card to log the next dose for that peptide in one click." },
      { h: "Body weight card", body: "Log today's weight inline. Your last 7 entries render as a mini trend. Set a goal weight in Profile to see distance-to-goal." },
      { h: "Weekly totals & streak", body: "Rolling 7-day shot count + longest consecutive-day streak. Miss a scheduled window and the streak resets — a gentle nudge to stay consistent. Streak Freeze protects up to 2 misses per quarter." },
      { h: "Announcement banner", body: "If the team publishes a site-wide notice, it appears as a colored strip at the top of every page. Read it and tap the X to dismiss — it won't come back.", flag: "announcements" },
      { h: "Quick Actions", body: "Grid of shortcuts: log dose, add vial, start cycle, browse library. Use these instead of digging through the nav." },
    ],
  },
  {
    id: "calculator",
    title: "Reconstitution Calculator",
    icon: Calculator,
    flag: "calculators",
    intro: "Get the exact number of units to draw for your target dose, on any syringe size.",
    steps: [
      { h: "Inputs", body: "Peptide mg (vial strength), BAC water mL (how much you reconstituted with), desired dose (mcg / mg / IU), and syringe size (30u / 50u / 100u)." },
      { h: "Reading the syringe visualizer", body: "The pointer + pill shows exactly where to pull the plunger. Units run cleanly along the bottom. The teal stats card confirms draw units, volume (mL), and doses-per-vial." },
      { h: "Multi-vial split dosing", body: "Toggle “Enable Split” to distribute one dose across multiple vials (great for finishing an almost-empty vial). Vials drain FIFO.", link: "/calculators", cta: "Open Calculator" },
    ],
  },
  {
    id: "library",
    title: "Peptide Library",
    icon: BookOpen,
    flag: "library",
    intro: "77 seeded compounds with recommended dosages, typical protocols, and one-click quick-log.",
    steps: [
      { h: "Browse & search", body: "Filter by name or category (Growth, Healing, Metabolic, Nootropic, Bioregulator, etc.). Each entry shows a recommended dosage + typical protocol at a glance." },
      { h: "Deep-dive & Quick Log", body: "Tap any peptide for full description, reconstitution defaults, and a “Quick Log” button that pre-fills the shot logger with sensible defaults.", link: "/library", cta: "Open Library" },
    ],
  },
  {
    id: "inventory",
    title: "Vial Inventory",
    icon: Package,
    flag: "inventory",
    intro: "Track what you have on hand. Vials auto-deduct as you log shots — no manual math.",
    steps: [
      { h: "Add a vial", body: "Peptide, mg amount, BAC water mL, purity, expiration, notes. remaining_mg starts equal to amount_mg and drops with each shot." },
      { h: "Auto-deduction", body: "When you log a shot, the oldest matching vial is drained first (FIFO). Dashboard shows a low-vial warning when remaining < 20%." },
      { h: "Edit or delete", body: "Tap a vial to open its detail sheet. Adjust remaining_mg manually if you spilled or measured differently.", link: "/inventory", cta: "Open Inventory" },
    ],
  },
  {
    id: "cycles",
    title: "Cycles",
    icon: Repeat,
    flag: "cycles",
    intro: "Plan runs ahead of time. Track adherence against actual logged shots.",
    steps: [
      { h: "Start a cycle", body: "Peptide, start & end date, dose, frequency (daily / weekly / custom), weekly target, notes." },
      { h: "Cycle Templates", body: "Pick from pre-defined blueprints (Retatrutide 12-week ramp, BPC-157 healing, GHK-Cu skin, Cortagen 10-day bioregulator, etc.) and drop the whole schedule onto your calendar in one tap." },
      { h: "Progress + adherence", body: "Cycle cards compute % complete against the calendar AND % adherence against actual logged shots.", link: "/cycles", cta: "Open Cycles" },
    ],
  },
  {
    id: "weight",
    title: "Body Weight",
    icon: Scale,
    flag: "weight",
    intro: "Log daily/weekly weight. See trend vs. goal without leaving the app.",
    steps: [
      { h: "Quick log", body: "One field, one tap. Timestamped automatically." },
      { h: "Trend chart", body: "Zoom out to 30 / 90 / 365 days. The dashed teal trajectory overlays your goal weight from Profile." },
      { h: "Weight streak", body: "Log at least once a week to keep your weight-tracking streak alive. Get a friendly Resend reminder if you're about to break it.", link: "/weight", cta: "Open Weight" },
    ],
  },
  {
    id: "supplements",
    title: "Supplements",
    icon: Pill,
    flag: "supplements",
    intro: "AI-parsed daily stack with a checklist that resets every day at midnight.",
    steps: [
      { h: "Describe your stack", body: "Type your routine in plain English — 'multivitamin at 8 AM, Magnesium at 9 PM'. Claude Sonnet 4.6 extracts each supplement + its time." },
      { h: "Daily checklist", body: "Every morning your stack shows up as a fresh checklist. Tap the circle to mark each one taken. At midnight, completions reset." },
      { h: "Dashboard progress", body: "Your daily completion percentage is pinned to the Dashboard next to Weight Insights.", link: "/supplements", cta: "Open Supplements" },
    ],
  },
  {
    id: "nutrition",
    title: "Nutrition & AI Meal Parser",
    icon: Utensils,
    flag: "nutrition",
    intro: "Track calories, protein, carbs, fat, sugar, fiber, and sodium. Describe, dictate, or photograph a meal — AI does the math.",
    steps: [
      { h: "Daily macro summary", body: "Today's Fuel card shows 7 macros vs. your goals with progress bars. Overshoots are flagged automatically." },
      { h: "AI Meal Parser (text)", body: "Type “two eggs, toast, and a coffee with milk” and Claude Sonnet 4.6 estimates macros for the whole meal. Tap any parsed item to refine the portion." },
      { h: "Voice-to-Nutrition (Whisper)", body: "Tap the mic 🎙, dictate your meal, and OpenAI Whisper transcribes → parses in one flow. Perfect for logging on-the-go.", flag: "ai_nutrition" },
      { h: "Meal Photo parsing", body: "Snap or upload a photo — Claude Vision identifies ingredients + estimates macros. Refine any portion afterward.", flag: "ai_nutrition" },
      { h: "Recent AI Parses ribbon", body: "The last 3 AI-parsed meals sit at the top with a one-tap “Log again” pill — perfect for repeat meals.", link: "/nutrition", cta: "Open Nutrition" },
    ],
  },
  {
    id: "coaching-clients",
    title: "Working with a coach",
    icon: ShieldCheck,
    flag: "coaching",
    intro: "Optionally share your progress with a verified coach for guidance, accountability, and personalized feedback.",
    steps: [
      { h: "Find a coach", body: "The Coach Directory on your Dashboard lists every verified coach — filter by specialty, rating, and payment options. Tap a coach to see their public bio + testimonials." },
      { h: "Request to share", body: "Tap “Share with this coach” on their card. They'll get an invite email; when they accept, they gain read-only access to your logs (shots, weight, nutrition, supplements)." },
      { h: "Chat + voice notes", body: "Once accepted, the Coach Chat panel appears on your Dashboard. Send text or voice notes (auto-transcribed via Whisper), and react with emojis." },
      { h: "Book a session", body: "If your coach has published booking slots, pick a time and confirm — a calendar invite lands in your inbox with a 30-minute Resend reminder before it starts." },
      { h: "Leave a rating", body: "After a session, share how it went. Ratings (with optional photos + response) surface on the coach's public profile — help future clients pick the right fit." },
      { h: "Revoke sharing anytime", body: "Open Profile → Sharing to revoke a coach's access instantly. Your data is yours — the coach loses read access the moment you tap Revoke.", link: "/dashboard", cta: "Back to Dashboard" },
    ],
  },
  {
    id: "history",
    title: "History & Body Heat-Map",
    icon: History,
    flag: "history",
    intro: "Every shot, weight, and meal — filter, edit, export.",
    steps: [
      { h: "Combined timeline", body: "Full CRUD across shots, weight entries, and meals. Filter by peptide, site, macro, or date. Delete or edit any past entry — vial remaining_mg is corrected automatically." },
      { h: "Heat-mapped body diagram", body: "18 injection points across Front & Back views. Colors: Rested / Moderate / Heavy / Never used — rotate your sites intelligently." },
      { h: "CSV / PDF export", body: "Bottom of the page: export the whole log (or the current filter). Handy for medical records or personal analysis.", link: "/history", cta: "Open History" },
    ],
  },
  {
    id: "community",
    title: "Community & Messages",
    icon: MessagesSquare,
    flag: "community",
    intro: "Discussion board with topics, threaded replies, DMs, live presence, and weekly recap sharing.",
    steps: [
      { h: "Topics & replies", body: "Start a topic (add attachments if useful), reply threaded. Green pulse dots next to usernames = online right now." },
      { h: "@Mentions", body: "Type @name in any reply to notify that user. They'll see it in their bell + optional email digest." },
      { h: "Weekly Recap sharing", body: "Share an auto-generated stats card summarizing your week (shots, macros, weight, adherence). Others can react with emojis." },
      { h: "Direct messages", body: "Message any staff (admin/moderator) directly. Staff can DM anyone. Regular users can only DM staff.", link: "/community", cta: "Open Community" },
    ],
  },
  {
    id: "feed",
    title: "Community Feed",
    icon: Users,
    flag: "feed",
    intro: "A live activity ticker for the whole community — latest topics, replies, recap shares, and streak milestones.",
    steps: [
      { h: "Realtime updates", body: "New activity streams in without a page reload. Filter by activity type." },
      { h: "Jump-in", body: "Tap any row to jump into the topic, DM, or profile.", link: "/feed", cta: "Open Feed" },
    ],
  },
  {
    id: "playbook",
    title: "Playbooks",
    icon: BookOpenCheck,
    flag: "playbook",
    intro: "Multi-lesson guides with videos, links, checklists, and premium-only content.",
    steps: [
      { h: "Preview lessons", body: "Any lesson marked “Preview” is free-to-view. The rest of a premium playbook is blurred until you unlock." },
      { h: "Track progress", body: "Mark lessons complete — a checkmark shows up on the sidebar. Your progress is saved server-side." },
      { h: "Unlock premium", body: "Tap “Unlock Premium” inside any locked lesson. Pay with PayPal or Venmo. Access flips instantly.", link: "/playbook", cta: "Open Playbook" },
    ],
  },
  {
    id: "streaks",
    title: "Streaks & Level-up",
    icon: Flame,
    intro: "Small wins, tracked forever. The app celebrates consistency, not perfection.",
    steps: [
      { h: "Dose streak", body: "Log a shot on schedule to keep your streak alive. Each week without a miss earns a small badge on your Profile." },
      { h: "Streak Freeze", body: "Life happens. You get 2 auto-freezes per quarter — misses inside that window won't reset your streak." },
      { h: "Leaderboard", body: "See how your streak stacks up against the top 10 in the community (opt-in; you can hide yourself in Profile)." },
      { h: "Level-up XP", body: "Points from logs, replies, and completed playbook lessons roll into an XP bar on your Profile.", link: "/profile", cta: "Open Profile" },
    ],
  },
  {
    id: "profile-notifications",
    title: "Profile & notifications",
    icon: User,
    intro: "Tune the app to you.",
    steps: [
      { h: "Personal info", body: "Name, avatar, body weight, height, timezone, default interval, macro goals (all 7)." },
      { h: "Browser notifications", body: "Toggle on to get a native OS notification when a dose is due. Works even if the tab is closed (via service worker)." },
      { h: "Email reminders", body: "Opt in to dose, weight, and weekly-recap emails delivered by Resend." },
      { h: "Password reset", body: "Public flow at /forgot-password. Token is single-use, 60-min TTL.", link: "/profile", cta: "Open Profile" },
    ],
  },
  {
    id: "shortcuts",
    title: "Keyboard shortcuts",
    icon: KeyRound,
    intro: "Move fast. Hit ? on any page to see the full list.",
    steps: [
      { h: "Navigation", body: "G then D → Dashboard · G then L → Library · G then I → Inventory · G then Y → Cycles · G then W → Weight · G then H → History · G then M → Community · G then P → Playbook · G then R → Profile." },
      { h: "Actions", body: "N → New shot · ⌘/Ctrl + K → Command palette · ? → Shortcut help." },
    ],
  },
];

/* ---------------- Tutorial data — COACH role ---------------- */
const coachSections = [
  {
    id: "coach-getting-started",
    title: "Becoming a coach",
    icon: Rocket,
    intro: "How the coaching flow works from your side. Read this first — it takes about a minute.",
    steps: [
      { h: "Get verified", body: "An admin turns on your Coach status from the admin panel. Once flipped, the “My Clients” tab appears in your Dashboard submenu and the onboarding wizard fires automatically." },
      { h: "Complete the onboarding wizard", body: "Set your specialties, session pricing, coaching bio, and payment links (Stripe / PayPal / Venmo). You can also mark yourself as accepting new clients or paused." },
      { h: "Go public (optional)", body: "Toggle Directory Visibility on and your card will show in the public Coach Directory on every user's Dashboard — filterable by specialty and rating." },
    ],
  },
  {
    id: "coach-clients",
    title: "Working with clients",
    icon: Users,
    intro: "Every user who shares progress with you shows up on your My Clients page. Read-only, always.",
    steps: [
      { h: "Client roster", body: "The main list on /clients shows everyone who accepted your invite. Search by handle/name, tap a card to open the full overview." },
      { h: "Client overview drawer", body: "See stats (body weight, goal weight, shot count, meal count), recent shots + nutrition + weights + supplements, plus their live Activity Feed. All read-only — you can't edit their logs." },
      { h: "Private notes", body: "Each client has a Coach Notes panel — jot follow-up thoughts, action items, or context that only you can see. Notes are threaded and timestamped." },
      { h: "Client insights", body: "The Activity Feed streams the last 30 days: doses logged, weights recorded, meals parsed, cycle changes. Perfect for prepping a session." },
    ],
  },
  {
    id: "coach-chat",
    title: "Coach Chat + voice notes",
    icon: MessageSquare,
    intro: "In-app messaging with each client. Text, voice, reactions — kept in one thread per client.",
    steps: [
      { h: "Text messages", body: "Open a client's card and use the chat panel on the right. Client sees your message on their Dashboard the moment you send it." },
      { h: "Voice notes with Whisper transcription", body: "Hold the mic button, dictate, release. Your voice sends as an audio blob PLUS an auto-transcript from OpenAI Whisper so clients can read too." },
      { h: "Reactions", body: "Long-press any message to react with an emoji. Great for quick “nice work” acknowledgments without cluttering the thread." },
      { h: "Push notifications", body: "Turn on Browser Notifications in your Profile — you'll get a native OS ping the moment a client replies, even if the tab is closed." },
    ],
  },
  {
    id: "coach-bookings",
    title: "Booking slots",
    icon: Clock,
    intro: "Let clients book time with you inside the app.",
    steps: [
      { h: "Publish slots", body: "Open My Clients → Booking Slots. Add a date, time, duration, and price. Clients see it on your public profile + inside chat." },
      { h: "Auto-reminders", body: "30 minutes before the slot, both you and the client get a Resend email nudge with the meeting link (if you added one)." },
      { h: "Cancel or reschedule", body: "Cancel a booking and the client is emailed with an optional reason. Slots stay yours to reuse." },
    ],
  },
  {
    id: "coach-ratings",
    title: "Ratings & public profile",
    icon: Star,
    intro: "Reputation lives on your public coach page.",
    steps: [
      { h: "How ratings work", body: "After a session, the client can leave a 1-5 star rating with an optional written review + photo. Ratings surface on your public /coach/:handle page." },
      { h: "Reply to a review", body: "Every review has a one-time public reply box. Say thanks or clarify — replies show inline under the star." },
      { h: "Hall-of-Fame section", body: "5-star reviews float to the top of your public page under a dedicated “Highlights” section — a social-proof boost for new clients." },
      { h: "Testimonial transformation cards", body: "Your best 5-star reviews can be exported as a shareable card (screenshot-friendly) for social media." },
    ],
  },
  {
    id: "coach-analytics",
    title: "Coach Analytics",
    icon: Activity,
    intro: "Numbers to help you see how your practice is doing.",
    steps: [
      { h: "Client activity heatmap", body: "Rolling 30-day heatmap of how active each of your clients has been. Instantly spot the ones going quiet." },
      { h: "Rating breakdown", body: "Average rating + count + distribution (how many 5s / 4s / 3s / 2s / 1s). Trend line over the last 90 days." },
      { h: "Engagement metrics", body: "Messages sent / received, avg response time, sessions booked vs. completed. All private — only you see these." },
    ],
  },
  {
    id: "coach-payments",
    title: "Payments",
    icon: Trophy,
    intro: "Get paid outside the app — we surface the links.",
    steps: [
      { h: "Add payment links", body: "In the onboarding wizard (or Profile → Coach Settings) add Stripe checkout, PayPal.me, or Venmo handles." },
      { h: "Client sees pay-now buttons", body: "Your links show under session bookings + on your public profile. Clients tap → pay directly via your provider (we don't take a cut)." },
      { h: "Track manually", body: "Payment status is mostly manual right now — mark a booking as “Paid” from the client card once you've received it." },
    ],
  },
];

/* ---------------- Tutorial data — ADMIN role ---------------- */
const adminSections = [
  {
    id: "admin-getting-started",
    title: "Admin panel overview",
    icon: Rocket,
    intro: "One page (/admin/settings) — every knob and dial. Tap a tile to jump in, hit “All settings” to jump back.",
    steps: [
      { h: "Where to go", body: "Open the avatar menu → Admin. You land on the tile grid. Each tile summarizes what lives inside; only tiles for enabled features render.", link: "/admin/settings", cta: "Open Admin Panel" },
      { h: "Take the guided tour", body: "The “Take the admin tour” button (top of the panel) walks you through every tile with a spotlight overlay. Perfect for onboarding a new admin." },
    ],
  },
  {
    id: "admin-user-activity",
    title: "User Activity",
    icon: Activity,
    intro: "Support triage — every user with last-login timestamp + recent actions.",
    steps: [
      { h: "Search + browse", body: "Debounced search (300ms) filters by email, name, or handle. Rows show role pill, coach badge, banned badge, and relative last-login time." },
      { h: "Inline recent actions", body: "Tap any row to expand the last 5 events with type icons (shots, weights, meals, community, coach shares)." },
      { h: "Full timeline drawer", body: "Click “Full timeline” to open a side drawer with up to 50 events for the user — perfect for diagnosing “where did my data go?” tickets." },
      { h: "Stealth observing", body: "Opening a user's activity does NOT touch their last_seen_at — the target has no idea you looked." },
    ],
  },
  {
    id: "admin-users",
    title: "User Management",
    icon: UserCog,
    intro: "Promote coaches, toggle moderator, ban, unban, reset password, view snapshot.",
    steps: [
      { h: "User list", body: "Search + filter by role. Every row surfaces email, name, role, joined date, and quick actions." },
      { h: "Promote to coach", body: "Toggle the Coach switch to give a user the Coach tab, directory listing, chat, ratings, and booking tools." },
      { h: "Ban with reason", body: "Ban writes an entry to the Ban Audit Log with your name + reason. Unban restores access; both actions email the user." },
      { h: "Password reset link", body: "Generate a one-time reset link (60-min TTL) and paste it into the user's DM or email — handy when they can't receive email." },
    ],
  },
  {
    id: "admin-coach-roster",
    title: "Coach Roster",
    icon: ShieldCheck,
    flag: "coaching",
    intro: "Add, message, feature, or remove any coach on the platform.",
    steps: [
      { h: "Add a coach", body: "Promote an existing user by email — one form field. If they don't have an account yet, ask them to register first." },
      { h: "Message one or all", body: "DM a single coach with subject + body (delivered via Resend + saved in history), or use “Broadcast to all” to blast every coach at once." },
      { h: "Feature a coach (pin)", body: "Tap the Pin icon to bubble a coach to the top of the public Directory. Amber highlight signals “admin-endorsed.”" },
      { h: "Remove with courtesy email", body: "Remove revokes coach status + every active client share. Provide an optional reason (max 600 chars) and it's emailed to the ex-coach." },
    ],
  },
  {
    id: "admin-super-view",
    title: "Super Admin Coaching View",
    icon: ShieldAlert,
    flag: "coaching",
    intro: "On /clients, admins see a global two-panel roster of every coach + every client with an active share.",
    steps: [
      { h: "Global search", body: "One search bar filters both panels by name, handle, email, or assigned coach — support tickets resolve in seconds." },
      { h: "Impersonate a coach", body: "“View as” button on any coach row — you see exactly what that coach sees on their /clients page (read-only)." },
      { h: "Reassign a client", body: "Inside a client drawer, tap Reassign, pick a new coach, done. Audit trail written to db.admin_share_reassignments." },
      { h: "Post an Admin note", body: "Drop an amber “Admin note” inside the client thread. The assigned coach sees it clearly badged in their normal notes panel." },
    ],
  },
  {
    id: "admin-announcements",
    title: "Announcements",
    icon: Megaphone,
    flag: "announcements",
    intro: "Publish a site-wide banner every user sees on login.",
    steps: [
      { h: "Publish or schedule", body: "Type the message, pick a tone (info / warning / good news), optionally set a datetime — future dates queue for later; past dates go live now. Only one active at a time." },
      { h: "Rich formatting", body: "Use **bold** to emphasize and [text](https://url) to link. Live preview panel shows the rendered banner. HTML in the body is escaped; unsafe URLs are stripped." },
      { h: "Reach analytics", body: "Every history row shows seen vs dismissed counts + a progress bar. Track how many users have engaged before retracting." },
      { h: "Retract or cancel", body: "One click to end a live announcement (Retract) or cancel a queued one before it goes live." },
    ],
  },
  {
    id: "admin-memberships",
    title: "Membership Management",
    icon: Crown,
    flag: "memberships",
    intro: "Track who's on Free / Pro / Coach and see revenue events at a glance.",
    steps: [
      { h: "Plan snapshot", body: "Summary chips at the top show total Pro, Coach, and Free counts. Tap any chip to filter the list to just that plan." },
      { h: "Grant Pro manually", body: "Any Free-tier user has a “Grant Pro” button. Add an optional reason (comped, annual paid via Stripe, beta reward, etc.) and it lands in the events feed for revenue tracking." },
      { h: "Revoke Pro", body: "Pro users get a “Revoke Pro” button in place of Grant. Their data is untouched — you can flip Pro back on anytime." },
      { h: "Revenue events log", body: "Every grant, revoke, and future paid subscription writes to the log at the bottom of the panel. Shows who / when / reason so you always know why a user's plan changed." },
      { h: "Turn the panel off", body: "Feature flag “Membership Management” — flip it off in Feature Flags to hide this tile if you don't need it yet." },
    ],
  },
  {
    id: "admin-community",
    title: "Community moderation",
    icon: Flag,
    intro: "Keep the discussion board healthy.",
    steps: [
      { h: "Categories", body: "Add / rename / reorder discussion categories. Empty categories can be safely deleted; the app protects against orphaned topics." },
      { h: "Moderation queue", body: "See flagged topics + replies + users. Approve, remove, or ban with a click. Ban Audit Log preserves every decision." },
      { h: "Ban Audit Log", body: "Historical view of every ban / unban with reason and admin name. Handy for compliance disputes." },
    ],
  },
  {
    id: "admin-branding",
    title: "Site Branding",
    icon: Palette,
    intro: "Name, tagline, logo, colors, favicon.",
    steps: [
      { h: "Name & tagline", body: "Set site name (top-left brand) + tagline that shows on the login page." },
      { h: "Logo + favicon", body: "Upload logo (PNG/SVG). Favicon is generated from it automatically." },
      { h: "Colors", body: "Primary accent is teal by default. Pick any hex — used across nav, buttons, and highlights." },
    ],
  },
  {
    id: "admin-install-preview",
    title: "Install Preview",
    icon: Eye,
    intro: "Live mockups of your site on iOS + Android home screens.",
    steps: [
      { h: "See what users see", body: "The preview updates in real time as you change the site name, logo, and colors — no need to actually install the PWA." },
      { h: "Screenshot for marketing", body: "Copy the mockup images for landing-page hero shots or press coverage." },
    ],
  },
  {
    id: "admin-landing",
    title: "Landing page",
    icon: Newspaper,
    intro: "Marketing copy, hero image, and CTAs for logged-out visitors.",
    steps: [
      { h: "Hero + CTA", body: "Edit the hero title, subtitle, and both CTA buttons. Changes apply instantly on your public / route." },
      { h: "Testimonials", body: "Rotating user quotes — add, edit, reorder, or delete. Weight them if you want a particular one to show first." },
    ],
  },
  {
    id: "admin-billing",
    title: "Billing & Payments",
    icon: Database,
    intro: "PayPal client id + premium playbook settings.",
    steps: [
      { h: "PayPal client id", body: "Paste your PayPal client id. The Buy Now buttons on premium playbooks auto-render with your account." },
      { h: "Premium settings", body: "Set the default premium price, currency, and success-return URL." },
    ],
  },
  {
    id: "admin-email",
    title: "Email Delivery",
    icon: Send,
    intro: "Resend + SMTP configuration for reminders, coach digests, password resets.",
    steps: [
      { h: "Resend (recommended)", body: "Paste your Resend API key + verified domain. All system emails (dose reminders, coach digests, announcements) send from a branded from address." },
      { h: "SMTP fallback", body: "If Resend isn't available in your region, plug in an SMTP host/port/user/pass instead. Same reliability." },
      { h: "Preview", body: "Send a test email to yourself with one click — validates your setup before the first real reminder fires." },
    ],
  },
  {
    id: "admin-feature-flags",
    title: "Feature Flags",
    icon: LayoutList,
    intro: "On/off switches for every major feature (15 total). Turn any section off and it disappears everywhere it appears on the site.",
    steps: [
      { h: "Everything toggleable", body: "Calculators · Library · Inventory · Cycles · Weight · Nutrition · Supplements · History · Feed · Tutorial · Community · Playbooks · Coach Ecosystem · AI Nutrition Parsing · Announcements." },
      { h: "How it hides", body: "Turning a flag OFF removes the nav link, hides the Dashboard card, and blocks direct-URL access (redirects to Dashboard). Turn it back ON and it reappears immediately." },
      { h: "Defense-in-depth note", body: "Backend endpoints stay open even when the flag is off — flags are for surface visibility. Add server-side gating later if you need harder isolation." },
    ],
  },
  {
    id: "admin-system-health",
    title: "System Health · Maintenance · Timezone",
    icon: Activity,
    intro: "The ops trifecta.",
    steps: [
      { h: "System Health", body: "Live probes — backend uptime, Mongo ping, Resend last-send status, background scheduler heartbeats. Latest error log tail underneath." },
      { h: "Maintenance mode", body: "Flip on to serve every user a friendly “back soon” page with your title + body + ETA. Admin can still get in with an ?admin_override=1 query." },
      { h: "Site Timezone", body: "Canonical time zone every date renders in unless a user has explicitly opted into their own local timezone in Profile." },
    ],
  },
];

/* ---------------- Component ---------------- */
export default function Tutorial() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("user");
  const [isCoach, setIsCoach] = useState(false);
  const isAdmin = user?.role === "admin";

  // Resolve coach status (admins auto-count as coaches for tab visibility)
  useEffect(() => {
    if (!user) return;
    if (isAdmin) { setIsCoach(true); return; }
    api.get("/coach/status")
      .then(({ data }) => setIsCoach(!!data.is_coach))
      .catch(() => setIsCoach(false));
  }, [user, isAdmin]);

  // Feature flag helper
  const flags = settings?.feature_flags || {};
  const isEnabled = (flag) => !flag || flags[flag] !== false;

  // Active sections for current role
  const activeSections = useMemo(() => {
    const raw = role === "coach" ? coachSections
             : role === "admin" ? adminSections
             : userSections;
    return raw
      .filter((s) => isEnabled(s.flag))
      .map((s) => ({ ...s, steps: s.steps.filter((st) => isEnabled(st.flag)) }))
      .filter((s) => s.steps.length > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, JSON.stringify(flags)]);

  const [activeId, setActiveId] = useState(activeSections[0]?.id || "");
  useEffect(() => {
    setActiveId(activeSections[0]?.id || "");
    setQ("");
  }, [role, activeSections.length]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return activeSections;
    return activeSections
      .map((s) => {
        const matchTitle = s.title.toLowerCase().includes(query) || s.intro.toLowerCase().includes(query);
        const steps = s.steps.filter((st) => (st.h + " " + st.body).toLowerCase().includes(query));
        if (matchTitle) return s;
        if (steps.length) return { ...s, steps };
        return null;
      })
      .filter(Boolean);
  }, [q, activeSections]);

  const roleTabs = [
    { key: "user",  label: "For users",  icon: User,        show: true },
    { key: "coach", label: "For coaches",icon: ShieldCheck, show: isCoach, flag: "coaching" },
    { key: "admin", label: "For admins", icon: ShieldAlert, show: isAdmin },
  ].filter((t) => t.show && isEnabled(t.flag));

  const heroEyebrow = role === "coach" ? "Coach Tutorial" : role === "admin" ? "Admin Tutorial" : "User Tutorial";
  const heroSubtitle = role === "coach"
    ? "How to run a great coaching practice inside The Amino Exchange — clients, chat, bookings, ratings, and analytics."
    : role === "admin"
      ? "Every dial in the admin panel — user activity, coach roster, announcements, feature flags, and more."
      : "A guided tour of every feature — set up your profile, run the calculator, track cycles, log meals with AI, and level up. Bookmark this page; it stays current as we ship.";

  return (
    <AppLayout>
      <PageHero
        eyebrow={heroEyebrow}
        title="Tutorial"
        icon={<GraduationCap className="w-10 h-10 text-[#0D9488]" />}
        subtitle={heroSubtitle}
      />

      {/* Role tabs — visible only when >1 role available */}
      {roleTabs.length > 1 && (
        <div className="mt-6 mb-4 inline-flex items-center gap-1 p-1 rounded-full bg-slate-100 border border-slate-200" data-testid="tutorial-role-tabs">
          {roleTabs.map((t) => {
            const Icon = t.icon;
            const active = role === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setRole(t.key)}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-semibold transition-colors ${
                  active ? "bg-white text-[#0D9488] shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid={`tutorial-role-tab-${t.key}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="mb-8 max-w-md relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search the ${role} tutorial…`}
          className="bg-white border-slate-200 pl-9 rounded-lg h-11 text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-[#0D9488]"
          data-testid="tutorial-search"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        {/* Sidebar TOC */}
        <aside className="lg:sticky lg:top-32 self-start" data-testid="tutorial-toc">
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 mb-3">Contents</div>
          <nav className="space-y-0.5">
            {filtered.map((s) => {
              const Icon = s.icon;
              const active = activeId === s.id;
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={() => setActiveId(s.id)}
                  className={`flex items-center gap-2 px-3 py-2 border-l-2 text-sm rounded-r transition-colors ${
                    active
                      ? "border-[#0D9488] bg-teal-50 text-[#0D9488]"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                  data-testid={`tutorial-toc-${s.id}`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{s.title}</span>
                </a>
              );
            })}
          </nav>

          {/* Admin hint */}
          {isAdmin && role === "user" && (
            <div className="mt-6 border border-slate-200 bg-slate-50 rounded-lg p-3" data-testid="tutorial-admin-hint">
              <div className="flex items-center gap-1.5 mb-1">
                <ShieldCheck className="w-3 h-3 text-[#0D9488]" />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[#0D9488]">Admin</span>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Sections auto-hide when you disable a feature in{" "}
                <Link to="/admin/settings" className="text-[#0D9488] hover:underline">Site Settings → Feature Flags</Link>.
              </p>
            </div>
          )}
        </aside>

        {/* Sections */}
        <div className="space-y-14" data-testid="tutorial-body">
          {filtered.length === 0 && (
            <div className="text-center py-16 border border-slate-200 bg-slate-50/40 rounded-lg">
              <div className="text-slate-500 text-sm">
                {q ? `No matches for “${q}”. Try a different keyword.` : "All tutorial sections are currently hidden by feature flags."}
              </div>
            </div>
          )}
          {filtered.map((s, idx) => {
            const Icon = s.icon;
            return (
              <section key={s.id} id={s.id} className="scroll-mt-32" data-testid={`tutorial-section-${s.id}`}>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400 tabular-nums">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="flex items-center gap-2.5">
                    <Icon className="w-6 h-6 text-[#0D9488]" strokeWidth={2.2} />
                    <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-slate-900">
                      {s.title}
                    </h2>
                  </div>
                </div>
                <p className="text-slate-600 text-base leading-relaxed mb-5 ml-9">{s.intro}</p>

                <ol className="space-y-3 ml-9">
                  {s.steps.map((st, i) => (
                    <li
                      key={`${s.id || s.title}-${i}-${(st.label || st.title || "").slice(0, 24)}`}
                      className="border border-slate-200 bg-white rounded-xl hover:border-teal-300 hover:shadow-sm transition-all p-5 group"
                    >
                      <div className="flex items-start gap-4">
                        <span className="font-display font-bold text-2xl text-teal-600/60 tabular-nums leading-none pt-0.5 shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display font-bold text-lg tracking-tight text-slate-900 mb-1">{st.h}</h3>
                          <p className="text-sm text-slate-600 leading-relaxed">{st.body}</p>
                          {st.link && (
                            <Link
                              to={st.link}
                              className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0D9488] hover:text-[#0F766E] group-hover:gap-2 transition-all"
                              data-testid={`tutorial-cta-${s.id}-${i}`}
                            >
                              {st.cta} <ChevronRight className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
